import React, { useEffect, useState } from "react";
import S from "./PartnerProducts.styled";
import { Flex, MainInner, MainSection, motionAnimation } from "@/styles/global";
import { Body, H3, SmallHeading1 } from "@/styles/typography";
import {
  Desktop,
  Icon,
  Input,
  LoadMoreButton,
  Mobile,
} from "@/styles/components";
import { IPartnerProducts, Taxonomy } from "@/src/types";
import { AnimatePresence, motion } from "framer-motion";
import { ActiveFilters, FilterDropdown } from "./Components";

export interface PartnerProductsProps {
  productsData?: IPartnerProducts;
  blockId: string;
}

interface ActiveFiltersState {
  categories: string[];
  deviceTypes: string[];
  sensorTypes: string[];
  offerings: string[];
  partners: string[];
}

type Filter = {
  key: string;
  label: string;
};

const filtersConfig = [
  { key: "categories", label: "Category" },
  { key: "deviceTypes", label: "Device type" },
  { key: "sensorTypes", label: "Sensor type" },
  { key: "offerings", label: "Offering" },
  { key: "partners", label: "Partner" },
];

const PartnerProducts: React.FC<PartnerProductsProps> = ({
  productsData,
  blockId = "",
}) => {
  const [search, setSearch] = useState<string>("");
  const [filteredData, setFilteredData] = useState<
    IPartnerProducts | undefined
  >([]);
  const [activeFilters, setActiveFilters] = useState<ActiveFiltersState>({
    categories: [],
    deviceTypes: [],
    sensorTypes: [],
    offerings: [],
    partners: [],
  });

  const [openFilter, setOpenFilter] = useState<Filter | null>(null);

  const [displayAmount, setDisplayAmount] = useState<number>(24);
  const onLoadMoreClick = () => setDisplayAmount(displayAmount + 24);

  const extractUniqueValues = (key: keyof ActiveFiltersState): string[] => [
    ...new Set(
      productsData?.flatMap((product) =>
        product[key].nodes.map((n) => n.name)
      ) ?? []
    ),
  ];

  const sortedValues = (key: keyof ActiveFiltersState): string[] =>
    extractUniqueValues(key).sort((a, b) => a.localeCompare(b));

  useEffect(() => {
    if (productsData) {
      setFilteredData(
        productsData.sort((a, b) =>
          a.productsInfo.title.localeCompare(b.productsInfo.title)
        )
      );
    }
  }, [productsData]);

  useEffect(() => {
    if (!productsData) return;

    const filterProducts = () => {
      return productsData.filter((product) => {
        const matchesSearch = (taxonomy: Taxonomy) =>
          taxonomy.nodes.some((node) =>
            node.name.toLowerCase().includes(search.toLowerCase())
          );

        const matchesFilter = (taxonomy: Taxonomy, filters: string[]) => {
          // If no filters are applied, return true
          if (filters.length === 0) return true;
          // OR operation for all filters
          return filters.some((filter) =>
            taxonomy.nodes.map((n) => n.name).includes(filter)
          );
        };

        return (
          (product.productsInfo.title
            .toLowerCase()
            .includes(search.toLowerCase()) ||
            filtersConfig.some((f) =>
              matchesSearch(product[f.key as keyof ActiveFiltersState])
            )) &&
          filtersConfig.every((f) =>
            matchesFilter(
              product[f.key as keyof ActiveFiltersState],
              activeFilters[f.key as keyof ActiveFiltersState]
            )
          )
        );
      });
    };

    setFilteredData(filterProducts());
  }, [search, activeFilters, productsData]);

  const toggleFilter = (key: keyof ActiveFiltersState, item: string) => {
    setActiveFilters((prev) => {
      const updatedFilters = prev[key].includes(item)
        ? prev[key].filter((i) => i !== item) // Remove the filter if it exists
        : [...prev[key], item]; // Add the filter if it doesn't exist

      // Update the URL with the modified filters
      if (typeof window !== "undefined") {
        const urlParams = new URLSearchParams(window.location.search);

        if (updatedFilters.length > 0) {
          // Update or add the filter to the URL if any active filters remain
          urlParams.set(key, updatedFilters.join(","));
        } else {
          // Remove the filter from the URL if no filters are active
          urlParams.delete(key);
        }

        window.history.replaceState(null, "", `?${urlParams.toString()}`);
      }

      return {
        ...prev,
        [key]: updatedFilters,
      };
    });
  };

  //parse url query params
  useEffect(() => {
    if (typeof window === "undefined") return;
    const urlParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlParams.entries());

    //if an anchor is present in the url, don't set search state
    if (window.location.hash) return;

    if (params.search) {
      setSearch(params.search);
    }

    filtersConfig.forEach((f) => {
      if (params[f.key]) {
        setActiveFilters((prev) => ({
          ...prev,
          [f.key]: params[f.key].split(","),
        }));
      }
    });

    //if there are query params, scroll mainSection to top
    if (Object.keys(params).length > 0 && mainSectionRef.current) {
      const offset = mainSectionRef.current.getBoundingClientRect().top;
      window.scrollTo(0, window.scrollY + offset);
      mainSectionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const urlParams = new URLSearchParams(window.location.search);
    //if an anchor is present in the url, don't set search state
    if (window.location.hash) return;
    if (window.location.href.indexOf("#") === -1) return;

    if (search) {
      urlParams.set("search", search);
    } else {
      urlParams.delete("search");
    }

    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${urlParams}`
    );
  }, [search]);

  const mainSectionRef = React.useRef<HTMLDivElement>(null);

  return (
    <S.Section as={motion.section} {...motionAnimation} id={blockId}>
      <S.Wrapper ref={mainSectionRef}>
        <MainSection>
          {openFilter && <S.Background onClick={() => setOpenFilter(null)} />}
          {/* MOBILE */}
          <Mobile>
            <Flex $column>
              <S.SearchWrapper>
                <Input
                  placeholder="Search product"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </S.SearchWrapper>
              <S.ScrollContainer>
                <S.Filters>
                  <Flex $gap={16} $desktopGap={32}>
                    {filtersConfig.map((filter, i) => (
                      <S.Filter
                        key={i}
                        onClick={() => setOpenFilter(filter)}
                        $active={
                          !openFilter
                            ? undefined
                            : openFilter.key === filter.key
                        }
                      >
                        <SmallHeading1>{filter.label}</SmallHeading1>
                        <Icon src="ChevronDown" width={8} margin="0 0 0 8px" />
                      </S.Filter>
                    ))}
                  </Flex>
                </S.Filters>
              </S.ScrollContainer>

              <AnimatePresence>
                {openFilter && (
                  <S.ActiveFilter
                    as={motion.div}
                    key={openFilter === undefined ? 1 : 0}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                  >
                    <Body weight={700} margin="0 0 18px 0">
                      {
                        filtersConfig.find((f) => f.key === openFilter.key)
                          ?.label
                      }
                    </Body>
                    <FilterDropdown
                      items={sortedValues(
                        openFilter.key as keyof ActiveFiltersState
                      )}
                      activeItems={
                        activeFilters[
                          openFilter.key as keyof ActiveFiltersState
                        ]
                      }
                      toggleItem={(item) =>
                        toggleFilter(
                          openFilter.key as keyof ActiveFiltersState,
                          item
                        )
                      }
                    />
                  </S.ActiveFilter>
                )}
              </AnimatePresence>
            </Flex>
          </Mobile>

          {/* DESKTOP */}
          <Desktop>
            <MainInner>
              <Flex $width={"100%"} $column>
                <S.SearchWrapper>
                  <Input
                    placeholder="Search product"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </S.SearchWrapper>
                <S.Filters>
                  {filtersConfig.map((filter, i) => (
                    <S.Filter
                      key={i}
                      onClick={() => setOpenFilter(filter)}
                      $active={
                        !openFilter ? undefined : openFilter.key === filter.key
                      }
                    >
                      <SmallHeading1>{filter.label}</SmallHeading1>
                      <Icon src="ChevronDown" width={8} margin="0 0 0 8px" />
                      <AnimatePresence>
                        {openFilter && openFilter.key === filter.key && (
                          <S.ActiveFilter
                            as={motion.div}
                            key={openFilter.key}
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                          >
                            <Body weight={700} margin="0 0 24px 0">
                              {
                                filtersConfig.find(
                                  (f) => f.key === openFilter.key
                                )?.label
                              }
                            </Body>
                            <FilterDropdown
                              items={sortedValues(
                                openFilter.key as keyof ActiveFiltersState
                              )}
                              activeItems={
                                activeFilters[
                                  openFilter.key as keyof ActiveFiltersState
                                ]
                              }
                              toggleItem={(item) =>
                                toggleFilter(
                                  openFilter.key as keyof ActiveFiltersState,
                                  item
                                )
                              }
                            />
                          </S.ActiveFilter>
                        )}
                      </AnimatePresence>
                    </S.Filter>
                  ))}
                </S.Filters>
              </Flex>
            </MainInner>
          </Desktop>

          <Desktop>
            <MainInner>
              <Flex
                $gap={8}
                $wrap="wrap"
                $margin="12px 0 32px"
                $height={50}
                as={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {filtersConfig.map((filter) => (
                  <ActiveFilters
                    key={filter.key}
                    activeFilters={
                      activeFilters[filter.key as keyof ActiveFiltersState]
                    }
                    removeFilter={(item) =>
                      toggleFilter(filter.key as keyof ActiveFiltersState, item)
                    }
                  />
                ))}
              </Flex>
            </MainInner>
          </Desktop>

          <AnimatePresence>
            <Mobile>
              <S.ScrollContainer>
                <S.Filters>
                  <Flex
                    $gap={8}
                    $margin="12px 0"
                    $height={50}
                    as={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    {filtersConfig.map((filter) => (
                      <ActiveFilters
                        key={filter.key}
                        activeFilters={
                          activeFilters[filter.key as keyof ActiveFiltersState]
                        }
                        removeFilter={(item) =>
                          toggleFilter(
                            filter.key as keyof ActiveFiltersState,
                            item
                          )
                        }
                      />
                    ))}
                  </Flex>
                </S.Filters>
              </S.ScrollContainer>
            </Mobile>
          </AnimatePresence>

          <MainInner>
            <S.NumberOfProducts>
              {filteredData?.length} products
            </S.NumberOfProducts>
            <S.Grid>
              {filteredData?.slice(0, displayAmount).map((product, index) => {
                const {
                  categories,
                  productsInfo,
                  deviceTypes,
                  offerings,
                  partners,
                  sensorTypes,
                } = product;
                return (
                  <S.Card
                    href={productsInfo?.link?.url}
                    target="_blank"
                    key={index}
                    as={motion.a}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <S.Image src={productsInfo.image} />
                    <S.TextContainer>
                      <H3 margin="0 0 8px 0">{productsInfo.title}</H3>

                      {categories.nodes.length > 0 && (
                        <Body weight={500} color="red" margin="0 0 16px 0">
                          {categories.nodes.map((n) => n.name).join(", ")}
                        </Body>
                      )}

                      {deviceTypes.nodes.length > 0 && (
                        <Body weight={400} margin="0 0 6px">
                          <span style={{ fontWeight: 700, marginRight: 4 }}>
                            Device type
                          </span>{" "}
                          {deviceTypes.nodes.map((n) => n.name).join(", ")}
                        </Body>
                      )}

                      {sensorTypes.nodes.length > 0 && (
                        <Body weight={400} margin="0 0 6px">
                          <span style={{ fontWeight: 700, marginRight: 4 }}>
                            Sensor type
                          </span>{" "}
                          {sensorTypes.nodes.map((n) => n.name).join(", ")}
                        </Body>
                      )}

                      {offerings.nodes.length > 0 && (
                        <Body weight={400} margin="0 0 6px">
                          <span style={{ fontWeight: 700, marginRight: 4 }}>
                            Offering
                          </span>{" "}
                          {offerings.nodes.map((n) => n.name).join(", ")}
                        </Body>
                      )}
                      {partners.nodes.length > 0 && (
                        <Body weight={400} margin="0 0 6px">
                          <span style={{ fontWeight: 700, marginRight: 4 }}>
                            Partner
                          </span>{" "}
                          {partners.nodes.map((n) => n.name).join(", ")}
                        </Body>
                      )}
                    </S.TextContainer>
                    <Flex $margin="auto 24px 16px">
                      <S.DetailsWrapper>
                        <S.Details>Details</S.Details>
                        <Icon src="ArrowRightOutline" width={14} color="red" />
                      </S.DetailsWrapper>
                    </Flex>
                  </S.Card>
                );
              })}
            </S.Grid>

            {filteredData && filteredData.length >= displayAmount && (
              <S.ButtonWrapper>
                <LoadMoreButton label="Load more" onClick={onLoadMoreClick} />
                <S.CurrentDisplayedAmount>
                  Displaying {displayAmount} / {filteredData.length} products
                </S.CurrentDisplayedAmount>
              </S.ButtonWrapper>
            )}

            {filteredData?.length === 0 && (
              <>
                <Flex $column $justify="center" $height={"30vh"}>
                  <Body weight={700} margin="0 0 12px 0">
                    No products found
                  </Body>
                  <Body>
                    Try changing your search query or filters to find products
                  </Body>
                </Flex>
              </>
            )}
          </MainInner>
        </MainSection>
      </S.Wrapper>
    </S.Section>
  );
};

export default PartnerProducts;
